import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './AgencyBookingRequests.css';
import { 
  getBookingRequests, 
  getBookingRequestById,
  acceptBookingRequest, 
  rejectBookingRequest,
  markStartLocationReached,
  markStartLocationDeparted,
  markStopReached,
  markStopDeparted,
  markDestinationDelivered
} from '../../services/bookingRequestsService';

const AgencyBookingRequests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingRequest, setTrackingRequest] = useState(null);
  const [showTrackingConfirm, setShowTrackingConfirm] = useState(false);
  const [trackingAction, setTrackingAction] = useState(null); // { type: 'reached'|'departed'|'delivered', requestId, stopIndex, isStartLocation, isDestination }
  const [toast, setToast] = useState(null); // { message, type: 'success'|'error' }

  const statusDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  // Mock booking requests data - shared by admin
  const initialRequests = [
    {
      id: 'REQ-001',
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+91 98765 43210',
      fromLocation: 'Chennai',
      toLocation: 'Bangalore',
      vehicleNumber: 'TN10AB1234',
      requestedDate: '2024-01-15 10:30 AM',
      status: 'Pending',
      notes: 'Urgent delivery required',
      sharedBy: 'Admin',
      sharedDate: '2024-01-15 09:00 AM',
      stops: [
        { location: 'Vellore', reached: false, departed: false, reachedTime: null, departedTime: null },
        { location: 'Tirupati', reached: false, departed: false, reachedTime: null, departedTime: null }
      ],
      products: [
        {
          productName: 'Electronics',
          productCategory: 'steel',
          customCategory: '',
          length: '120',
          height: '80',
          width: '60',
          weight: '500'
        },
        {
          productName: 'Mobile Phones',
          productCategory: 'others',
          customCategory: 'Electronics',
          length: '30',
          height: '20',
          width: '10',
          weight: '50'
        }
      ]
    },
    {
      id: 'REQ-002',
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      phone: '+91 98765 43211',
      fromLocation: 'Mumbai',
      toLocation: 'Delhi',
      vehicleNumber: 'MH02EF9012',
      requestedDate: '2024-01-14 09:15 AM',
      status: 'Confirmed',
      notes: 'Standard delivery',
      sharedBy: 'Admin',
      sharedDate: '2024-01-14 08:00 AM',
      stops: [
        { location: 'Nashik', reached: false, departed: false, reachedTime: null, departedTime: null },
        { location: 'Aurangabad', reached: false, departed: false, reachedTime: null, departedTime: null }
      ],
      products: [
        {
          productName: 'Textile Materials',
          productCategory: 'crackers',
          customCategory: '',
          length: '200',
          height: '150',
          width: '100',
          weight: '2500'
        }
      ]
    },
    {
      id: 'REQ-003',
      name: 'Robert Johnson',
      email: 'robert.j@example.com',
      phone: '+91 98765 43212',
      fromLocation: 'Hyderabad',
      toLocation: 'Pune',
      vehicleNumber: 'TS05GH3456',
      requestedDate: '2024-01-13 11:20 AM',
      status: 'Rejected',
      notes: 'Rejected due to capacity constraints',
      sharedBy: 'Admin',
      sharedDate: '2024-01-13 10:00 AM',
      products: [
        {
          productName: 'Machinery Parts',
          productCategory: 'steel',
          customCategory: '',
          length: '300',
          height: '200',
          width: '150',
          weight: '8000'
        }
      ]
    },
    {
      id: 'REQ-004',
      name: 'Priya Sharma',
      email: 'priya.sharma@example.com',
      phone: '+91 98765 43213',
      fromLocation: 'Kolkata',
      toLocation: 'Chennai',
      vehicleNumber: 'WB07IJ7890',
      requestedDate: '2024-01-12 08:45 AM',
      status: 'Pending',
      notes: 'Waiting for agency response',
      sharedBy: 'Admin',
      sharedDate: '2024-01-12 07:30 AM',
      products: [
        {
          productName: 'Food Products',
          productCategory: 'vegetables',
          customCategory: '',
          length: '150',
          height: '100',
          width: '80',
          weight: '3000'
        }
      ]
    },
    {
      id: 'REQ-005',
      name: 'Amit Patel',
      email: 'amit.patel@example.com',
      phone: '+91 98765 43214',
      fromLocation: 'Bangalore',
      toLocation: 'Mumbai',
      vehicleNumber: 'KA01CD5678',
      requestedDate: '2024-01-11 02:30 PM',
      status: 'Pending',
      notes: 'High priority booking',
      sharedBy: 'Admin',
      sharedDate: '2024-01-11 01:00 PM',
      products: [
        {
          productName: 'Automotive Parts',
          productCategory: 'others',
          customCategory: 'Auto Components',
          length: '180',
          height: '120',
          width: '90',
          weight: '4500'
        }
      ]
    }
  ];

  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [requestToAction, setRequestToAction] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Valid statuses from API: Pending, Contacted, Confirmed, Cancelled, Rejected
  const availableStatuses = ['Pending', 'Contacted', 'Confirmed', 'Cancelled', 'Rejected'];

  // Map API response to component format
  const mapBookingRequestFromAPI = useCallback((apiRequest) => {
    // Format date from API (ISO format)
    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return dateString;
      }
    };

    // Map tracking status fields from API
    // The backend stores tracking information in the notes field as text entries
    // Format: [TRACKING] Start location reached at 2025-12-26T11:08:11.800Z
    const mapTrackingStatus = () => {
      // First, check if API returns direct tracking fields (snake_case or camelCase)
      const hasDirectFields = apiRequest.start_location_reached !== undefined || 
                             apiRequest.startLocationReached !== undefined ||
                             apiRequest.start_location_reached_at !== undefined;
      
      if (hasDirectFields) {
        // Use direct fields if available
        const startReached = apiRequest.start_location_reached !== undefined ? apiRequest.start_location_reached :
                            apiRequest.startLocationReached !== undefined ? apiRequest.startLocationReached :
                            apiRequest.start_location_reached_at ? true : false;
        
        const startDeparted = apiRequest.start_location_departed !== undefined ? apiRequest.start_location_departed :
                             apiRequest.startLocationDeparted !== undefined ? apiRequest.startLocationDeparted :
                             apiRequest.start_location_departed_at ? true : false;
        
        const destReached = apiRequest.destination_reached !== undefined ? apiRequest.destination_reached :
                           apiRequest.destinationReached !== undefined ? apiRequest.destinationReached :
                           apiRequest.destination_reached_at ? true : false;

        const formatTrackingTime = (timestamp) => {
          if (!timestamp) return null;
          if (timestamp === true) return null;
          try {
            return formatDate(timestamp);
          } catch (e) {
            return timestamp;
          }
        };

        return {
          startLocationReached: !!startReached,
          startLocationReachedTime: formatTrackingTime(apiRequest.start_location_reached_at || apiRequest.startLocationReachedAt),
          startLocationDeparted: !!startDeparted,
          startLocationDepartedTime: formatTrackingTime(apiRequest.start_location_departed_at || apiRequest.startLocationDepartedAt),
          destinationReached: !!destReached,
          destinationReachedTime: formatTrackingTime(apiRequest.destination_reached_at || apiRequest.destinationReachedAt)
        };
      }

      // Fallback: Parse tracking information from notes field
      // Format: [TRACKING] Start location reached at 2025-12-26T11:08:11.800Z
      const parseTrackingFromNotes = (notes) => {
        if (!notes || typeof notes !== 'string') {
          return {
            startLocationReached: false,
            startLocationReachedTime: null,
            startLocationDeparted: false,
            startLocationDepartedTime: null,
            destinationReached: false,
            destinationReachedTime: null
          };
        }

        const trackingLines = notes.split('\n').filter(line => line.includes('[TRACKING]'));
        
        let startLocationReached = false;
        let startLocationReachedTime = null;
        let startLocationDeparted = false;
        let startLocationDepartedTime = null;
        let destinationReached = false;
        let destinationReachedTime = null;

        // Process tracking lines in chronological order
        trackingLines.forEach(line => {
          const match = line.match(/\[TRACKING\]\s+(.+?)\s+at\s+(.+)/);
          if (!match) return;

          const event = match[1].toLowerCase().trim();
          const timestamp = match[2].trim();

          if (event.includes('start location reached')) {
            startLocationReached = true;
            startLocationReachedTime = timestamp; // Keep the latest one
          } else if (event.includes('start location departed')) {
            startLocationDeparted = true;
            startLocationDepartedTime = timestamp; // Keep the latest one
          } else if (event.includes('destination delivered') || event.includes('destination reached')) {
            destinationReached = true;
            destinationReachedTime = timestamp; // Keep the latest one
          }
        });

        // Format timestamps
        const formatTrackingTime = (timestamp) => {
          if (!timestamp) return null;
          try {
            return formatDate(timestamp);
          } catch (e) {
            return timestamp;
          }
        };

        return {
          startLocationReached,
          startLocationReachedTime: formatTrackingTime(startLocationReachedTime),
          startLocationDeparted,
          startLocationDepartedTime: formatTrackingTime(startLocationDepartedTime),
          destinationReached,
          destinationReachedTime: formatTrackingTime(destinationReachedTime)
        };
      };

      const trackingStatus = parseTrackingFromNotes(apiRequest.notes);
      console.log('Parsed tracking status from notes:', trackingStatus);
      return trackingStatus;
    };

    // Map stops with tracking information
    const mapStops = () => {
      if (!apiRequest.stops || !Array.isArray(apiRequest.stops)) {
        return [];
      }
      return apiRequest.stops.map((stop, index) => {
        // Handle both object format and array format
        const stopObj = typeof stop === 'object' ? stop : { location: stop };
        return {
          location: stopObj.location || stopObj.stop_location || stop || '',
          reached: stopObj.reached || 
                  stopObj.reached_at || 
                  stopObj.actual_arrival_time || 
                  false,
          reachedTime: formatDate(
            stopObj.reached_at || 
            stopObj.actual_arrival_time || 
            stopObj.reachedTime
          ),
          departed: stopObj.departed || 
                   stopObj.departed_at || 
                   stopObj.actual_departure_time || 
                   false,
          departedTime: formatDate(
            stopObj.departed_at || 
            stopObj.actual_departure_time || 
            stopObj.departedTime
          )
        };
      });
    };

    const trackingStatus = mapTrackingStatus();
    const mappedStops = mapStops();

    return {
      // Store both numeric id (for API calls) and request_id (for display)
      id: apiRequest.id || apiRequest.request_id || '',
      numericId: apiRequest.id || null, // Numeric ID for API calls
      requestId: apiRequest.request_id || apiRequest.id?.toString() || '', // Display ID
      name: apiRequest.name || '',
      email: apiRequest.email || '',
      phone: apiRequest.phone || '',
      fromLocation: apiRequest.from_location || '',
      toLocation: apiRequest.to_location || '',
      vehicleNumber: apiRequest.vehicle_type || apiRequest.vehicle_number || '',
      requestedDate: formatDate(apiRequest.requested_date || apiRequest.created_at),
      status: apiRequest.status || 'Pending',
      notes: apiRequest.notes || '',
      sharedBy: 'Admin', // This is set by admin when sharing
      sharedDate: formatDate(apiRequest.created_at),
      stops: mappedStops,
      products: (apiRequest.products || []).map(product => ({
        productName: product.product_name || '',
        productCategory: product.product_category || '',
        customCategory: product.custom_category || '',
        length: product.length_cm?.toString() || '',
        height: product.height_cm?.toString() || '',
        width: product.width_cm?.toString() || '',
        weight: product.weight_kg?.toString() || ''
      })),
      // Include tracking status fields
      ...trackingStatus
    };
  }, []);

  // Pagination - API handles filtering and pagination
  const totalPages = Math.ceil(totalRequests / itemsPerPage);
  const paginatedRequests = requests; // API already returns paginated results

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch booking requests from API
  const fetchBookingRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await getBookingRequests(params);
      
      // Extract booking requests from response
      let requestsData = [];
      if (response.data) {
        requestsData = response.data.items || response.data.requests || response.data.data || [];
      } else if (Array.isArray(response)) {
        requestsData = response;
      } else if (response.items || response.requests) {
        requestsData = response.items || response.requests;
      }

      // Map API response to component format
      const mappedRequests = requestsData.map(request => mapBookingRequestFromAPI(request));
      
      setRequests(mappedRequests);
      
      // Get total count from response
      const total = response.data?.total || response.total || response.data?.count || mappedRequests.length;
      setTotalRequests(total);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      setError(err.message || 'Failed to fetch booking requests. Please try again.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, mapBookingRequestFromAPI]);

  // Fetch booking requests on mount and when filters change
  useEffect(() => {
    fetchBookingRequests();
  }, [fetchBookingRequests]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  const handleResetFilters = () => {
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleViewRequest = (request) => {
    setViewingRequest(request);
    setShowViewModal(true);
  };

  const handleAcceptRequest = (requestIdOrRequest) => {
    // Handle both direct ID and request object
    const request = typeof requestIdOrRequest === 'object' ? requestIdOrRequest : requests.find(r => r.id === requestIdOrRequest || r.requestId === requestIdOrRequest);
    const numericId = request?.numericId || request?.id;
    
    if (!numericId || isNaN(parseInt(numericId, 10))) {
      alert('Invalid booking request ID. Please refresh the page and try again.');
      return;
    }
    
    setRequestToAction(numericId);
    setShowAcceptConfirm(true);
  };

  const handleRejectRequest = (requestIdOrRequest) => {
    // Handle both direct ID and request object
    const request = typeof requestIdOrRequest === 'object' ? requestIdOrRequest : requests.find(r => r.id === requestIdOrRequest || r.requestId === requestIdOrRequest);
    const numericId = request?.numericId || request?.id;
    
    if (!numericId || isNaN(parseInt(numericId, 10))) {
      alert('Invalid booking request ID. Please refresh the page and try again.');
      return;
    }
    
    setRequestToAction(numericId);
    setShowRejectConfirm(true);
  };

  const handleTrackingClick = async (request) => {
    try {
      // Get numericId for API call
      const numericId = request.numericId || 
                       (request.id && !isNaN(parseInt(request.id, 10)) ? parseInt(request.id, 10) : null);
      
      if (!numericId) {
        console.error('Invalid booking request ID for tracking:', request);
        alert('Unable to load tracking information. Invalid request ID.');
        return;
      }

      // Fetch full booking request details with tracking information
      console.log('Fetching full booking request details for tracking, ID:', numericId);
      const response = await getBookingRequestById(numericId);
      
      // Extract the booking request data from response
      let requestData = null;
      if (response.data) {
        requestData = response.data.request || response.data.booking_request || response.data;
      } else if (response.request || response.booking_request) {
        requestData = response.request || response.booking_request;
      } else {
        requestData = response;
      }

      if (!requestData) {
        console.error('No booking request data in API response:', response);
        // Fallback to using the request from the list
        const latestRequest = requests.find(r => 
          (r.numericId && request.numericId && r.numericId === request.numericId) ||
          (r.id && request.id && r.id === request.id) ||
          (r.requestId && request.requestId && r.requestId === request.requestId)
        ) || request;
        setTrackingRequest(latestRequest);
        setShowTrackingModal(true);
        return;
      }

      // Log the raw API response to see what fields are available
      console.log('Raw API response for booking request:', JSON.stringify(requestData, null, 2));
      console.log('All keys in API response:', Object.keys(requestData));
      
      // Map the full booking request data (includes tracking status)
      const mappedRequest = mapBookingRequestFromAPI(requestData);
      
      console.log('Mapped tracking request with full details:', mappedRequest);
      console.log('Tracking status in mapped request:', {
        startLocationReached: mappedRequest.startLocationReached,
        startLocationDeparted: mappedRequest.startLocationDeparted,
        destinationReached: mappedRequest.destinationReached
      });
      setTrackingRequest(mappedRequest);
      setShowTrackingModal(true);
    } catch (err) {
      console.error('Error fetching booking request details for tracking:', err);
      // Fallback to using the request from the list if API call fails
      const latestRequest = requests.find(r => 
        (r.numericId && request.numericId && r.numericId === request.numericId) ||
        (r.id && request.id && r.id === request.id) ||
        (r.requestId && request.requestId && r.requestId === request.requestId)
      ) || request;
      
      // Ensure numericId is set for tracking
      if (!latestRequest.numericId) {
        if (latestRequest.id) {
          const parsedId = parseInt(latestRequest.id, 10);
          if (!isNaN(parsedId)) {
            latestRequest.numericId = parsedId;
          }
        }
      }
      
      console.log('Using request from list (fallback):', latestRequest);
      setTrackingRequest(latestRequest);
      setShowTrackingModal(true);
    }
  };

  // Update trackingRequest when requests change (sync with server data)
  useEffect(() => {
    if (trackingRequest) {
      // Try to find the updated request by numericId, id, or requestId
      const updatedRequest = requests.find(r => 
        (r.numericId && trackingRequest.numericId && r.numericId === trackingRequest.numericId) ||
        (r.id && trackingRequest.id && r.id === trackingRequest.id) ||
        (r.requestId && trackingRequest.requestId && r.requestId === trackingRequest.requestId)
      );
      if (updatedRequest) {
        // Merge to preserve any local UI state that hasn't been synced yet
        setTrackingRequest(prev => ({
          ...updatedRequest,
          // Preserve local state if server hasn't updated yet
          startLocationReached: updatedRequest.startLocationReached || prev?.startLocationReached,
          startLocationDeparted: updatedRequest.startLocationDeparted || prev?.startLocationDeparted,
          destinationReached: updatedRequest.destinationReached || prev?.destinationReached,
          startLocationReachedTime: updatedRequest.startLocationReachedTime || prev?.startLocationReachedTime,
          startLocationDepartedTime: updatedRequest.startLocationDepartedTime || prev?.startLocationDepartedTime,
          destinationReachedTime: updatedRequest.destinationReachedTime || prev?.destinationReachedTime,
        }));
      }
    }
  }, [requests]);

  const handleMarkReached = (requestIdOrRequest, stopIndex, isStartLocation = false, isDestination = false) => {
    // Handle both direct ID and request object
    let numericId;
    
    if (typeof requestIdOrRequest === 'object' && requestIdOrRequest !== null) {
      // It's a request object
      numericId = requestIdOrRequest.numericId || requestIdOrRequest.id;
    } else {
      // It's an ID - try to find the request or use it directly
      const request = requests.find(r => 
        r.numericId === requestIdOrRequest || 
        r.id === requestIdOrRequest || 
        r.requestId === requestIdOrRequest
      );
      numericId = request?.numericId || request?.id || requestIdOrRequest;
    }
    
    // Validate numeric ID
    const parsedId = typeof numericId === 'string' ? parseInt(numericId, 10) : numericId;
    if (!numericId || isNaN(parsedId)) {
      console.error('Invalid booking request ID:', numericId, 'from request:', requestIdOrRequest);
      alert('Invalid booking request ID. Please refresh the page and try again.');
      return;
    }
    
    setTrackingAction({
      type: isDestination ? 'delivered' : 'reached',
      requestId: parsedId,
      stopIndex,
      isStartLocation,
      isDestination
    });
    setShowTrackingConfirm(true);
  };

  const handleMarkDeparted = (requestIdOrRequest, stopIndex, isStartLocation = false) => {
    // Handle both direct ID and request object
    let numericId;
    
    if (typeof requestIdOrRequest === 'object' && requestIdOrRequest !== null) {
      // It's a request object
      numericId = requestIdOrRequest.numericId || requestIdOrRequest.id;
    } else {
      // It's an ID - try to find the request or use it directly
      const request = requests.find(r => 
        r.numericId === requestIdOrRequest || 
        r.id === requestIdOrRequest || 
        r.requestId === requestIdOrRequest
      );
      numericId = request?.numericId || request?.id || requestIdOrRequest;
    }
    
    // Validate numeric ID
    const parsedId = typeof numericId === 'string' ? parseInt(numericId, 10) : numericId;
    if (!numericId || isNaN(parsedId)) {
      console.error('Invalid booking request ID:', numericId, 'from request:', requestIdOrRequest);
      alert('Invalid booking request ID. Please refresh the page and try again.');
      return;
    }
    
    setTrackingAction({
      type: 'departed',
      requestId: parsedId,
      stopIndex,
      isStartLocation,
      isDestination: false
    });
    setShowTrackingConfirm(true);
  };

  const confirmTrackingAction = async () => {
    if (!trackingAction) return;

    const { type, requestId, stopIndex, isStartLocation, isDestination } = trackingAction;
    
    // requestId should already be numeric from handleMarkReached/handleMarkDeparted
    // But let's validate it anyway
    const numericId = typeof requestId === 'string' ? parseInt(requestId, 10) : requestId;
    
    if (!numericId || isNaN(numericId)) {
      console.error('Invalid booking request ID in tracking action:', requestId, 'trackingAction:', trackingAction);
      alert('Invalid booking request ID. Please refresh the page and try again.');
      setShowTrackingConfirm(false);
      setTrackingAction(null);
      return;
    }
    
    console.log('Confirming tracking action:', { type, numericId, stopIndex, isStartLocation, isDestination });

    setIsProcessingAction(true);
    try {
      let response;
      
      // Call appropriate API based on action type
      if (type === 'reached') {
        if (isStartLocation) {
          response = await markStartLocationReached(numericId);
          // Update trackingRequest immediately for instant UI feedback
          if (trackingRequest) {
            setTrackingRequest({
              ...trackingRequest,
              startLocationReached: true,
              startLocationReachedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            });
          }
        } else if (isDestination) {
          response = await markDestinationDelivered(numericId);
          // Update trackingRequest immediately for instant UI feedback
          if (trackingRequest) {
            setTrackingRequest({
              ...trackingRequest,
              destinationReached: true,
              destinationReachedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            });
          }
        } else {
          response = await markStopReached(numericId, stopIndex);
          // Update trackingRequest immediately for instant UI feedback
          if (trackingRequest && trackingRequest.stops && trackingRequest.stops[stopIndex]) {
            const updatedStops = [...trackingRequest.stops];
            updatedStops[stopIndex] = {
              ...updatedStops[stopIndex],
              reached: true,
              reachedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            };
            setTrackingRequest({
              ...trackingRequest,
              stops: updatedStops
            });
          }
        }
      } else if (type === 'departed') {
        if (isStartLocation) {
          response = await markStartLocationDeparted(numericId);
          // Update trackingRequest immediately for instant UI feedback
          if (trackingRequest) {
            setTrackingRequest({
              ...trackingRequest,
              startLocationDeparted: true,
              startLocationDepartedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            });
          }
        } else {
          response = await markStopDeparted(numericId, stopIndex);
          // Update trackingRequest immediately for instant UI feedback
          if (trackingRequest && trackingRequest.stops && trackingRequest.stops[stopIndex]) {
            const updatedStops = [...trackingRequest.stops];
            updatedStops[stopIndex] = {
              ...updatedStops[stopIndex],
              departed: true,
              departedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            };
            setTrackingRequest({
              ...trackingRequest,
              stops: updatedStops
            });
          }
        }
      } else if (type === 'delivered') {
        response = await markDestinationDelivered(numericId);
        // Update trackingRequest immediately for instant UI feedback
        if (trackingRequest) {
          setTrackingRequest({
            ...trackingRequest,
            destinationReached: true,
            destinationReachedTime: new Date().toLocaleString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })
          });
        }
      }

      // Close confirmation modal immediately
      setShowTrackingConfirm(false);
      setTrackingAction(null);

      // Refresh booking requests list in the background to sync with server
      // The useEffect hook will update trackingRequest with server data when it arrives
      fetchBookingRequests().catch(err => {
        console.error('Error refreshing booking requests:', err);
        // Don't show error to user, just log it
      });
    } catch (err) {
      console.error('Error updating tracking:', err);
      alert(err.message || 'Failed to update tracking. Please try again.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const confirmAccept = async () => {
    if (!requestToAction) return;
    
    setIsProcessingAction(true);
    try {
      // Call API to accept the booking request
      await acceptBookingRequest(requestToAction);
      
      // Refresh the booking requests list
      await fetchBookingRequests();
      
      setShowAcceptConfirm(false);
      setRequestToAction(null);
      setShowViewModal(false);
      setViewingRequest(null);
      setToast({ message: 'Booking request accepted successfully!', type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error('Error accepting booking request:', err);
      setToast({ message: err.message || 'Failed to accept booking request. Please try again.', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsProcessingAction(false);
    }
  };

  const confirmReject = async () => {
    if (!requestToAction) return;
    
    setIsProcessingAction(true);
    try {
      // Call API to reject the booking request
      await rejectBookingRequest(requestToAction);
      
      // Refresh the booking requests list
      await fetchBookingRequests();
      
      setShowRejectConfirm(false);
      setRequestToAction(null);
      setShowViewModal(false);
      setViewingRequest(null);
      setToast({ message: 'Booking request rejected successfully.', type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error('Error rejecting booking request:', err);
      setToast({ message: err.message || 'Failed to reject booking request. Please try again.', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsProcessingAction(false);
    }
  };

  const filteredStatuses = availableStatuses.filter(status =>
    status.toLowerCase().includes(statusSearch.toLowerCase())
  );

  const getStatusClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'status-pending';
      case 'Contacted':
        return 'status-contacted';
      case 'Confirmed':
        return 'status-confirmed';
      case 'Cancelled':
        return 'status-cancelled';
      case 'Rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  return (
    <div className="booking-requests-container">
      {/* Header */}
      <div className="requests-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            >
              <span>Status: {statusFilter || 'All'}</span>
              <span>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <input
                  type="text"
                  placeholder="Search status..."
                  className="filter-search-input"
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                />
                <button
                  className={`filter-option ${!statusFilter ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                    setStatusSearch('');
                  }}
                >
                  All
                </button>
                {filteredStatuses.map(status => (
                  <button
                    key={status}
                    className={`filter-option ${statusFilter === status ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                      setStatusSearch('');
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="btn-reset" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>
      </div>

      {/* Show Entries and Search */}
      <div className="show-entries-container">
        <div className="show-entries-wrapper">
          <div className="show-entries-left">
            <span className="show-entries-label">Show</span>
            <div className="filter-wrapper" ref={showEntriesDropdownRef}>
              <button
                className="show-entries-btn"
                onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
              >
                <span>{itemsPerPage}</span>
                <span>▼</span>
              </button>
              {showEntriesDropdownOpen && (
                <div className="filter-dropdown">
                  {[10, 25, 50, 100].map(num => (
                    <button
                      key={num}
                      className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                      onClick={() => {
                        setItemsPerPage(num);
                        setShowEntriesDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="show-entries-label">entries</span>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, phone, location, vehicle, or request ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container" style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '18px', color: '#666' }}>Loading booking requests...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-container" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
          <button 
            className="btn-retry" 
            onClick={fetchBookingRequests}
            style={{ padding: '10px 20px', background: '#2563EB', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
      <div className="requests-table-wrapper">
        <table className="requests-table">
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Name</th>
              <th>Contact</th>
              <th>From Location</th>
              <th>To Location</th>
              <th>Vehicle Number</th>
              <th>Requested Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRequests.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No booking requests found
                </td>
              </tr>
            ) : (
              paginatedRequests.map(request => (
                <tr key={request.id}>
                  <td>{request.requestId || request.id}</td>
                  <td>{request.name}</td>
                  <td>
                    <div className="contact-info">
                      <div>{request.email}</div>
                      <div className="phone-number">{request.phone}</div>
                    </div>
                  </td>
                  <td>{request.fromLocation}</td>
                  <td>{request.toLocation}</td>
                  <td>{request.vehicleNumber}</td>
                  <td>{request.requestedDate}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleViewRequest(request)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      {request.status === 'Pending' && (
                        <>
                          <button
                            className="btn-accept"
                            onClick={() => handleAcceptRequest(request)}
                            title="Accept Request"
                          >
                            ✓ Accept
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleRejectRequest(request)}
                            title="Reject Request"
                          >
                            ✗ Reject
                          </button>
                        </>
                      )}
                      {(request.status === 'Contacted' || request.status === 'Confirmed') && (
                        <button
                          className="btn-tracking"
                          onClick={() => handleTrackingClick(request)}
                          title="Track Shipment"
                        >
                          📍 Track
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {!loading && !error && (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <div className="pagination-count">
            Showing {totalRequests === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, totalRequests)} of {totalRequests} entries
          </div>
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
      )}

      {/* View Request Modal */}
      {showViewModal && viewingRequest && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Request Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="view-details">
                <div className="detail-row">
                  <span className="detail-label">Request ID:</span>
                  <span className="detail-value">{viewingRequest.requestId || viewingRequest.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{viewingRequest.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{viewingRequest.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{viewingRequest.phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">From Location:</span>
                  <span className="detail-value">{viewingRequest.fromLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Location:</span>
                  <span className="detail-value">{viewingRequest.toLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Vehicle Number:</span>
                  <span className="detail-value">{viewingRequest.vehicleNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Requested Date:</span>
                  <span className="detail-value">{viewingRequest.requestedDate}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <span className={`status-badge ${getStatusClass(viewingRequest.status)}`}>
                      {viewingRequest.status}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value">{viewingRequest.notes || 'No notes'}</span>
                </div>
              </div>

              {/* Product Details Section */}
              {viewingRequest.products && viewingRequest.products.length > 0 && (
                <div className="product-details-section">
                  <h3 className="section-title">Product Details</h3>
                  {viewingRequest.products.map((product, index) => (
                    <div key={index} className="product-item-detail" style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e3a8a', marginBottom: '12px' }}>
                        Product {index + 1}
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Product Name:</span>
                        <span className="detail-value">{product.productName}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Category:</span>
                        <span className="detail-value">
                          {product.productCategory === 'others' ? product.customCategory : product.productCategory.charAt(0).toUpperCase() + product.productCategory.slice(1)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Size:</span>
                        <span className="detail-value">
                          {product.length}cm × {product.height}cm × {product.width}cm
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{product.weight} kg</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Shared Information */}
              <div className="shared-info-section">
                <h3 className="section-title">Shared Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Shared By:</span>
                  <span className="detail-value">{viewingRequest.sharedBy || 'Admin'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Shared Date:</span>
                  <span className="detail-value">{viewingRequest.sharedDate || 'N/A'}</span>
                </div>
              </div>

              {/* Accept/Reject Actions */}
              {viewingRequest.status === 'Pending' && (
                <div className="action-section">
                  <h3 className="section-title">Actions</h3>
                  <div className="action-buttons-modal">
                    <button
                      className="btn-accept-modal"
                      onClick={() => handleAcceptRequest(viewingRequest)}
                    >
                      ✓ Accept Request
                    </button>
                    <button
                      className="btn-reject-modal"
                      onClick={() => handleRejectRequest(viewingRequest)}
                    >
                      ✗ Reject Request
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Confirmation Modal */}
      {showAcceptConfirm && (
        <div className="modal-overlay" onClick={() => setShowAcceptConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Accept Booking Request</h2>
              <button className="modal-close" onClick={() => setShowAcceptConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to accept this booking request?</p>
              <p className="confirm-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAcceptConfirm(false)}>
                Cancel
              </button>
              <button className="btn-confirm-accept" onClick={confirmAccept}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && (
        <div className="modal-overlay" onClick={() => setShowRejectConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Booking Request</h2>
              <button className="modal-close" onClick={() => setShowRejectConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject this booking request?</p>
              <p className="confirm-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRejectConfirm(false)}>
                Cancel
              </button>
              <button 
                className="btn-confirm-reject" 
                onClick={confirmReject}
                disabled={isProcessingAction}
              >
                {isProcessingAction ? 'Processing...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {showTrackingModal && trackingRequest && (
        <div className="modal-overlay" onClick={() => setShowTrackingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Track Shipment - {trackingRequest.requestId || trackingRequest.id}</h2>
              <button className="modal-close" onClick={() => setShowTrackingModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Start Location */}
                <div style={{ 
                  padding: '20px', 
                  background: '#f9fafb', 
                  borderRadius: '8px', 
                  border: '2px solid #e0e0e0',
                  borderLeft: '4px solid #10B981'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                      🏁 Start Location
                    </h3>
                    {trackingRequest.startLocationReached && (
                      <span style={{ 
                        padding: '4px 12px', 
                        background: '#10B981', 
                        color: 'white', 
                        borderRadius: '12px', 
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}>
                        ✓ Reached
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '8px 0', fontSize: '1rem', fontWeight: 500, color: '#111827' }}>
                    {trackingRequest.fromLocation}
                  </p>
                  {trackingRequest.startLocationReachedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Reached: {trackingRequest.startLocationReachedTime}
                    </p>
                  )}
                  {trackingRequest.startLocationDepartedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Departed: {trackingRequest.startLocationDepartedTime}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    {!trackingRequest.startLocationReached && (
                      <button
                        onClick={() => handleMarkReached(trackingRequest, -1, true, false)}
                        style={{
                          padding: '8px 16px',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        ✓ Mark Reached
                      </button>
                    )}
                    {trackingRequest.startLocationReached && !trackingRequest.startLocationDeparted && (
                      <button
                        onClick={() => handleMarkDeparted(trackingRequest, -1, true)}
                        style={{
                          padding: '8px 16px',
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        🚚 Mark Departed
                      </button>
                    )}
                  </div>
                </div>

                {/* Intermediate Stops */}
                {trackingRequest.stops && trackingRequest.stops.length > 0 && (
                  <>
                    {trackingRequest.stops.map((stop, index) => (
                      <div key={index} style={{ 
                        padding: '20px', 
                        background: '#f9fafb', 
                        borderRadius: '8px', 
                        border: '2px solid #e0e0e0',
                        borderLeft: '4px solid #2563EB'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                            📍 Stop {index + 1}: {stop.location}
                          </h3>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {stop.reached && (
                              <span style={{ 
                                padding: '4px 12px', 
                                background: '#10B981', 
                                color: 'white', 
                                borderRadius: '12px', 
                                fontSize: '0.85rem',
                                fontWeight: 500
                              }}>
                                ✓ Reached
                              </span>
                            )}
                            {stop.departed && (
                              <span style={{ 
                                padding: '4px 12px', 
                                background: '#EF4444', 
                                color: 'white', 
                                borderRadius: '12px', 
                                fontSize: '0.85rem',
                                fontWeight: 500
                              }}>
                                🚚 Departed
                              </span>
                            )}
                          </div>
                        </div>
                        {stop.reachedTime && (
                          <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                            Reached: {stop.reachedTime}
                          </p>
                        )}
                        {stop.departedTime && (
                          <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                            Departed: {stop.departedTime}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          {!stop.reached && (
                            <button
                              onClick={() => handleMarkReached(trackingRequest, index, false, false)}
                              style={{
                                padding: '8px 16px',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 500
                              }}
                            >
                              ✓ Mark Reached
                            </button>
                          )}
                          {stop.reached && !stop.departed && (
                            <button
                              onClick={() => handleMarkDeparted(trackingRequest, index, false)}
                              style={{
                                padding: '8px 16px',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 500
                              }}
                            >
                              🚚 Mark Departed
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Destination */}
                <div style={{ 
                  padding: '20px', 
                  background: '#f9fafb', 
                  borderRadius: '8px', 
                  border: '2px solid #e0e0e0',
                  borderLeft: '4px solid #EF4444'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                      🎯 Destination
                    </h3>
                    {trackingRequest.destinationReached && (
                      <span style={{ 
                        padding: '4px 12px', 
                        background: '#10B981', 
                        color: 'white', 
                        borderRadius: '12px', 
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}>
                        ✓ Delivered
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '8px 0', fontSize: '1rem', fontWeight: 500, color: '#111827' }}>
                    {trackingRequest.toLocation}
                  </p>
                  {trackingRequest.destinationReachedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Delivered: {trackingRequest.destinationReachedTime}
                    </p>
                  )}
                  {!trackingRequest.destinationReached && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        onClick={() => handleMarkReached(trackingRequest, -1, false, true)}
                        style={{
                          padding: '8px 16px',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        ✓ Mark Delivered
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTrackingModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Confirmation Modal */}
      {showTrackingConfirm && trackingAction && trackingRequest && (
        <div className="modal-overlay" onClick={() => setShowTrackingConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Confirm Tracking Update</h2>
              <button className="modal-close" onClick={() => setShowTrackingConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '1rem', color: '#333', margin: '16px 0' }}>
                {trackingAction.type === 'reached' && trackingAction.isStartLocation && 
                  `Are you sure you want to mark the start location "${trackingRequest.fromLocation}" as reached?`}
                {trackingAction.type === 'reached' && !trackingAction.isStartLocation && !trackingAction.isDestination && 
                  `Are you sure you want to mark stop "${trackingRequest.stops?.[trackingAction.stopIndex]?.location}" as reached?`}
                {trackingAction.type === 'delivered' && 
                  `Are you sure you want to mark the destination "${trackingRequest.toLocation}" as delivered?`}
                {trackingAction.type === 'departed' && trackingAction.isStartLocation && 
                  `Are you sure you want to mark the start location "${trackingRequest.fromLocation}" as departed?`}
                {trackingAction.type === 'departed' && !trackingAction.isStartLocation && 
                  `Are you sure you want to mark stop "${trackingRequest.stops?.[trackingAction.stopIndex]?.location}" as departed?`}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTrackingConfirm(false)}>
                Cancel
              </button>
              <button 
                className="btn-share" 
                onClick={confirmTrackingAction}
                disabled={isProcessingAction}
                style={{ background: '#10B981' }}
              >
                {isProcessingAction ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyBookingRequests;

